// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noImplicitAnyLet: TODO - address later
/**
 * This component handles numeric, date, and text filter types.
 * Use FilterSelect from @/components/ui/filter-select.tsx for multi-select filters only.
 *
 * IMPORTANT: During the refactoring of filter components, the implementation of number and date
 * filter types was not included in the new FilterSelect component. This component should be used
 * for those filter types until proper implementations are available in FilterSelect.
 */

import { Filter, ListFilter } from "lucide-react";
import {
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DatePickerInput } from "@/components/date-picker-input";
import { Button } from "@/components/ui/button";
import { FilterSelect } from "@/components/ui/filter-select";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDateToISO, safeParseDateString } from "@/lib/date-utils";
import { isFilterValueActive } from "@/lib/filter-value-utils";
import { cn } from "@/lib/utils";
import type { UnifiedTableColumn } from "@/types/table-column";

// Define types for filter values - export them so they can be used elsewhere
export type NumberFilter = {
  min?: string;
  max?: string;
  nullFilter?: "is_null" | "is_not_null" | null;
};

export type DateFilter = {
  from?: string;
  to?: string;
  nullFilter?: "is_null" | "is_not_null" | null;
};

export type TextFilter = {
  value?: string;
  nullFilter?: "is_null" | "is_not_null" | null;
};

export type SelectFilter = {
  values?: string[];
  nullFilter?: "is_null" | "is_not_null" | null;
};

export type SelectOption = {
  id: string;
  label: string;
};

export type FilterValue =
  | string
  | string[]
  | NumberFilter
  | DateFilter
  | TextFilter
  | SelectFilter
  | null;

export interface FilterPopoverProps {
  column: UnifiedTableColumn;
  currentFilter: FilterValue;
  onFilterChange: (key: string, value: FilterValue) => void;
  onClearFilter: (key: string) => void;
  showNullFilter?: boolean;
  /** Render only inner panel without popover shell */
  standalone?: boolean;
  /** Optional controlled open state */
  open?: boolean;
  /** Callback for controlled open change */
  onOpenChange?: (open: boolean) => void;
  /** Callback invoked when panel wishes to close */
  onRequestClose?: () => void;
  /** Extra class name to apply to trigger button */
  buttonClassName?: string;
  /** Optional ref to trigger button */
  triggerRef?: Ref<HTMLButtonElement>;
}

// Helper component for null filter selection
function NullFilterSelect({
  value,
  onChange,
  disabled = false,
  onNullFilterChange,
}: {
  value?: "is_null" | "is_not_null" | null;
  onChange: (value: "is_null" | "is_not_null" | null) => void;
  disabled?: boolean;
  onNullFilterChange?: (value: "is_null" | "is_not_null" | null) => void;
}) {
  const handleChange = (val: string) => {
    const nullFilterValue =
      val === "unset" ? null : (val as "is_null" | "is_not_null");

    if (onNullFilterChange) {
      onNullFilterChange(nullFilterValue);
    } else {
      onChange(nullFilterValue);
    }
  };

  return (
    <Select
      value={value || "unset"}
      onValueChange={handleChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-fit min-w-[140px]">
        <SelectValue placeholder="Has value" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="unset">has value</SelectItem>
        <SelectItem value="is_not_null">is not null</SelectItem>
        <SelectItem value="is_null">is null</SelectItem>
      </SelectContent>
    </Select>
  );
}

export function FilterPopover({
  column,
  currentFilter,
  onFilterChange,
  onClearFilter,
  showNullFilter = true,
  open: controlledOpen,
  onOpenChange,
  buttonClassName,
  triggerRef,
  standalone = true,
  onRequestClose,
}: FilterPopoverProps) {
  // Support both controlled and uncontrolled open state
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = typeof controlledOpen === "boolean";
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpenInternal =
    isControlled && onOpenChange ? onOpenChange : setUncontrolledOpen;
  // Expose close helper
  const closePanel = () => {
    setOpenInternal(false);
    onRequestClose?.();
  };
  const [isInnerSelectOpen, setIsInnerSelectOpen] = useState(false);

  // Helper to check if the filter type is select
  const isSelectFilter = useMemo(() => {
    return column.filterType === "select";
  }, [column.filterType]);

  // Initialize localFilter based on filterType
  const initializeFilter = useCallback(() => {
    if (isSelectFilter) {
      // For select type, convert to SelectFilter object
      if (
        currentFilter &&
        typeof currentFilter === "object" &&
        !Array.isArray(currentFilter)
      ) {
        return currentFilter as SelectFilter;
      }
      // Convert array to SelectFilter object
      if (Array.isArray(currentFilter)) {
        return { values: currentFilter } as SelectFilter;
      }
      return {} as SelectFilter;
    }

    // For number filters
    if (column.filterType === "number") {
      // Ensure we have a proper number filter object
      if (
        currentFilter &&
        typeof currentFilter === "object" &&
        !Array.isArray(currentFilter)
      ) {
        return currentFilter as NumberFilter;
      }
      return {} as NumberFilter;
    }

    // For date filters
    if (column.filterType === "date") {
      // Ensure we have a proper date filter object
      if (
        currentFilter &&
        typeof currentFilter === "object" &&
        !Array.isArray(currentFilter)
      ) {
        return currentFilter as DateFilter;
      }
      return {} as DateFilter;
    }

    // For text filters - convert string to TextFilter object
    if (column.filterType === "text") {
      if (
        currentFilter &&
        typeof currentFilter === "object" &&
        !Array.isArray(currentFilter)
      ) {
        return currentFilter as TextFilter;
      }
      // Convert string to TextFilter object
      if (typeof currentFilter === "string") {
        return { value: currentFilter } as TextFilter;
      }
      return {} as TextFilter;
    }

    // Default for other filters
    return currentFilter || "";
  }, [isSelectFilter, currentFilter, column.filterType]);

  const [localFilter, setLocalFilter] = useState<FilterValue>(
    initializeFilter(),
  );
  const prevCurrentFilterRef = useRef(currentFilter);

  // Update localFilter when currentFilter changes externally
  useEffect(() => {
    // Skip if the current filter is the same as the previous one
    if (
      JSON.stringify(prevCurrentFilterRef.current) ===
      JSON.stringify(currentFilter)
    ) {
      return;
    }

    // Calculate the value inside the effect instead of depending on the callback
    let newValue;
    if (column.filterType === "select") {
      // Handle select filter conversion
      if (
        currentFilter &&
        typeof currentFilter === "object" &&
        !Array.isArray(currentFilter)
      ) {
        newValue = currentFilter as SelectFilter;
      } else if (Array.isArray(currentFilter)) {
        newValue = { values: currentFilter } as SelectFilter;
      } else {
        newValue = {} as SelectFilter;
      }
    } else if (column.filterType === "text") {
      // Handle text filter conversion
      if (
        currentFilter &&
        typeof currentFilter === "object" &&
        !Array.isArray(currentFilter)
      ) {
        newValue = currentFilter as TextFilter;
      } else if (typeof currentFilter === "string") {
        newValue = { value: currentFilter } as TextFilter;
      } else {
        newValue = {} as TextFilter;
      }
    } else {
      newValue = currentFilter || "";
    }

    // Update the local filter
    setLocalFilter(newValue);

    // Update the ref
    prevCurrentFilterRef.current = currentFilter;
  }, [currentFilter, column.filterType]);

  // Check if filter is active using the centralized utility
  const isFilterActive = () => {
    return isFilterValueActive(localFilter);
  };

  const applyFilterAndClose = (filter: FilterValue) => {
    // Use the centralized utility to check if the filter is meaningful
    if (isFilterValueActive(filter)) {
      onFilterChange(column.key, filter);
    } else {
      onFilterChange(column.key, null);
    }
    closePanel();
  };

  const handleApply = () => {
    applyFilterAndClose(localFilter);
  };

  const handleCancel = () => {
    // Reset local filter to current filter without applying changes
    setLocalFilter(initializeFilter());
    closePanel();
  };

  const handleClear = () => {
    if (isSelectFilter) {
      setLocalFilter({} as SelectFilter);
    } else if (column.filterType === "number") {
      setLocalFilter({} as NumberFilter);
    } else if (column.filterType === "date") {
      setLocalFilter({} as DateFilter);
    } else if (column.filterType === "text") {
      setLocalFilter({} as TextFilter);
    } else {
      setLocalFilter("");
    }
    onClearFilter(column.key);
    closePanel();
  };

  // Improved change detection using isFilterValueActive
  const hasFilterChanges = (current: FilterValue, original: FilterValue) => {
    // Check if both filters are inactive (no meaningful content)
    const currentActive = isFilterValueActive(current);
    const originalActive = isFilterValueActive(original);

    // If both are inactive, no changes
    if (!currentActive && !originalActive) {
      return false;
    }

    // If only one is active, there are changes
    if (currentActive !== originalActive) {
      return true;
    }

    // Both are active, check for content differences
    return JSON.stringify(current) !== JSON.stringify(original);
  };

  // Handle popover close - only apply changes if there are meaningful modifications
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && open) {
      // If the inner select was open, it will have already applied the filter directly
      if (isInnerSelectOpen) {
        closePanel();
        return;
      }

      // Check if the local filter has actually changed meaningfully
      const hasChanges = hasFilterChanges(localFilter, currentFilter);

      // Only apply changes if there have been actual meaningful modifications
      if (hasChanges) {
        handleApply();
      } else {
        // No meaningful changes made, just close without applying
        closePanel();
      }
    } else {
      setOpenInternal(newOpen);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const renderFilterContent = () => {
    switch (column.filterType) {
      case "select": {
        const selectFilter = (localFilter as SelectFilter) || {};
        const isNullFilterActive =
          selectFilter.nullFilter === "is_null" ||
          selectFilter.nullFilter === "is_not_null";

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {showNullFilter && (
                <NullFilterSelect
                  value={selectFilter.nullFilter}
                  onChange={(nullFilter) =>
                    setLocalFilter({ ...selectFilter, nullFilter })
                  }
                  onNullFilterChange={(nullFilter) => {
                    if (
                      nullFilter === "is_null" ||
                      nullFilter === "is_not_null"
                    ) {
                      // Clear other filters when null filter is selected
                      setLocalFilter({ nullFilter, values: [] });
                    } else {
                      setLocalFilter({ ...selectFilter, nullFilter });
                    }
                  }}
                />
              )}
              <div
                className={cn(
                  "flex-1",
                  isNullFilterActive && "opacity-50 pointer-events-none",
                )}
                onClick={(e) => {
                  if (isNullFilterActive) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                <FilterSelect
                  column={column}
                  currentFilter={selectFilter.values || []}
                  onFilterChange={(_, value) => {
                    if (!isNullFilterActive) {
                      const newValues = Array.isArray(value) ? value : [];
                      const updatedFilter = {
                        ...selectFilter,
                        values: newValues,
                      };
                      setLocalFilter(updatedFilter);

                      // If this is being called during close (when isInnerSelectOpen is true),
                      // directly apply the complete filter to bypass cascade issues
                      if (isInnerSelectOpen) {
                        applyFilterAndClose(updatedFilter);
                      }
                    }
                  }}
                  onClearFilter={() => {
                    if (!isNullFilterActive) {
                      const updatedFilter = { ...selectFilter, values: [] };
                      setLocalFilter(updatedFilter);

                      // If this is being called during close, directly apply
                      if (isInnerSelectOpen) {
                        applyFilterAndClose(updatedFilter);
                      }
                    }
                  }}
                  isTableFilter={false}
                  width="100%"
                  onOpenChange={setIsInnerSelectOpen}
                />
              </div>
            </div>
          </div>
        );
      }
      case "text": {
        const textFilter = (localFilter as TextFilter) || {};
        const isNullFilterActive =
          textFilter.nullFilter === "is_null" ||
          textFilter.nullFilter === "is_not_null";

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {showNullFilter && (
                <NullFilterSelect
                  value={textFilter.nullFilter}
                  onChange={(nullFilter) =>
                    setLocalFilter({ ...textFilter, nullFilter })
                  }
                  onNullFilterChange={(nullFilter) => {
                    if (
                      nullFilter === "is_null" ||
                      nullFilter === "is_not_null"
                    ) {
                      // Clear other filters when null filter is selected
                      setLocalFilter({ nullFilter });
                    } else {
                      setLocalFilter({ ...textFilter, nullFilter });
                    }
                  }}
                />
              )}
              <div
                className={cn(
                  "flex-1",
                  isNullFilterActive && "opacity-50 pointer-events-none",
                )}
              >
                <Input
                  placeholder="Enter text to filter..."
                  onKeyDown={(e) => e.stopPropagation()}
                  value={textFilter.value || ""}
                  data-testid="filter-input"
                  onChange={(e) => {
                    if (!isNullFilterActive) {
                      const newValue = e.target.value;
                      if (newValue.trim() === "") {
                        // Remove the value property when empty
                        const filterWithoutValue = Object.fromEntries(
                          Object.entries(textFilter).filter(
                            ([key]) => key !== "value",
                          ),
                        );
                        setLocalFilter(filterWithoutValue);
                      } else {
                        setLocalFilter({ ...textFilter, value: newValue });
                      }
                    }
                  }}
                  disabled={isNullFilterActive}
                />
              </div>
            </div>
          </div>
        );
      }
      case "number": {
        const numberFilter = (localFilter as NumberFilter) || {};
        const isNullFilterActive =
          numberFilter.nullFilter === "is_null" ||
          numberFilter.nullFilter === "is_not_null";

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {showNullFilter && (
                <NullFilterSelect
                  value={numberFilter.nullFilter}
                  onChange={(nullFilter) =>
                    setLocalFilter({ ...numberFilter, nullFilter })
                  }
                  onNullFilterChange={(nullFilter) => {
                    if (
                      nullFilter === "is_null" ||
                      nullFilter === "is_not_null"
                    ) {
                      // Clear other filters when null filter is selected
                      setLocalFilter({ nullFilter });
                    } else {
                      setLocalFilter({ ...numberFilter, nullFilter });
                    }
                  }}
                />
              )}
              <div
                className={cn(
                  "flex-1",
                  isNullFilterActive && "opacity-50 pointer-events-none",
                )}
              >
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    onKeyDown={(e) => e.stopPropagation()}
                    value={numberFilter.min || ""}
                    onChange={(e) => {
                      if (!isNullFilterActive) {
                        const newValue = e.target.value;
                        if (newValue.trim() === "") {
                          // Remove the min property when empty
                          const filterWithoutMin = Object.fromEntries(
                            Object.entries(numberFilter).filter(
                              ([key]) => key !== "min",
                            ),
                          );
                          setLocalFilter(filterWithoutMin);
                        } else {
                          setLocalFilter({
                            ...numberFilter,
                            min: newValue,
                          });
                        }
                      }
                    }}
                    disabled={isNullFilterActive}
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="number"
                    placeholder="Max"
                    onKeyDown={(e) => e.stopPropagation()}
                    value={numberFilter.max || ""}
                    onChange={(e) => {
                      if (!isNullFilterActive) {
                        const newValue = e.target.value;
                        if (newValue.trim() === "") {
                          // Remove the max property when empty
                          const filterWithoutMax = Object.fromEntries(
                            Object.entries(numberFilter).filter(
                              ([key]) => key !== "max",
                            ),
                          );
                          setLocalFilter(filterWithoutMax);
                        } else {
                          setLocalFilter({
                            ...numberFilter,
                            max: newValue,
                          });
                        }
                      }
                    }}
                    disabled={isNullFilterActive}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }
      case "date": {
        const dateFilter = (localFilter as DateFilter) || {};
        const isNullFilterActive =
          dateFilter.nullFilter === "is_null" ||
          dateFilter.nullFilter === "is_not_null";

        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {showNullFilter && (
                <NullFilterSelect
                  value={dateFilter.nullFilter}
                  onChange={(nullFilter) =>
                    setLocalFilter({ ...dateFilter, nullFilter })
                  }
                  onNullFilterChange={(nullFilter) => {
                    if (
                      nullFilter === "is_null" ||
                      nullFilter === "is_not_null"
                    ) {
                      // Clear other filters when null filter is selected
                      setLocalFilter({ nullFilter });
                    } else {
                      setLocalFilter({ ...dateFilter, nullFilter });
                    }
                  }}
                />
              )}
              <div
                className={cn(
                  "flex-1",
                  isNullFilterActive && "opacity-50 pointer-events-none",
                )}
              >
                <div className="flex items-center gap-1">
                  <div className="relative flex-1">
                    <DatePickerInput
                      value={dateFilter.from}
                      onChange={(date) => {
                        if (!isNullFilterActive) {
                          if (!date) {
                            // Remove the from property when empty
                            const filterWithoutFrom = Object.fromEntries(
                              Object.entries(dateFilter).filter(
                                ([key]) => key !== "from",
                              ),
                            );
                            setLocalFilter(filterWithoutFrom);
                          } else {
                            setLocalFilter({
                              ...dateFilter,
                              from: formatDateToISO(date),
                            });
                          }
                        }
                      }}
                      disabled={isNullFilterActive}
                      maxDate={
                        dateFilter.to
                          ? safeParseDateString(dateFilter.to)
                          : undefined
                      }
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                  <span className="text-muted-foreground text-sm">to</span>
                  <div className="relative flex-1">
                    <DatePickerInput
                      value={dateFilter.to}
                      onChange={(date) => {
                        if (!isNullFilterActive) {
                          if (!date) {
                            // Remove the to property when empty
                            const filterWithoutTo = Object.fromEntries(
                              Object.entries(dateFilter).filter(
                                ([key]) => key !== "to",
                              ),
                            );
                            setLocalFilter(filterWithoutTo);
                          } else {
                            setLocalFilter({
                              ...dateFilter,
                              to: formatDateToISO(date),
                            });
                          }
                        }
                      }}
                      disabled={isNullFilterActive}
                      minDate={
                        dateFilter.from
                          ? safeParseDateString(dateFilter.from)
                          : undefined
                      }
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const panelBody = (
    <PopoverContent
      className="w-auto min-w-96"
      onKeyDown={handleKeyDown}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="space-y-4">
        {renderFilterContent()}
        <div className="flex justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            data-testid="filter-clear-button"
          >
            Clear
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleApply();
              }}
              data-testid="apply-filter-button"
            >
              Apply Filter
            </Button>
          </div>
        </div>
      </div>
    </PopoverContent>
  );

  if (!standalone) {
    return panelBody;
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant={isFilterActive() ? "outline" : "ghost"}
          size="icon"
          className={cn(
            "h-6 w-6 p-0",
            isFilterActive() ? "text-primary" : "text-muted-foreground",
            buttonClassName,
          )}
        >
          {isFilterActive() ? (
            <Filter className="h-3 w-3" />
          ) : (
            <ListFilter className="h-3 w-3" />
          )}
        </Button>
      </PopoverTrigger>
      {panelBody}
    </Popover>
  );
}
